import React, { useState, useCallback, useRef, useEffect } from 'react'
import ReactMarkdown from 'react-markdown'
import styled from 'shared/theme'
import PageLayout from 'pages/util/PageLayout'
import { useAuthSnowplowPageView } from 'shared/hooks/useSnowPlowPageView'
import { AuthContext } from 'auth/Auth'
import { sendAiChatMessage } from 'api/aiChat'

const Container = styled.div`
    display: flex;
    flex-direction: column;
    height: calc(100vh - 4rem);
    max-width: 1200px;
    margin: 0 auto;
    padding: 1rem 2rem;
`

const Title = styled.h1`
    font-size: 1.5rem;
    font-weight: 600;
    margin-bottom: 1rem;
    color: ${props => props.theme.colors.textPrimary};
`

const ChatContainer = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    border: 1px solid ${props => props.theme.colors.border};
    border-radius: 8px;
    overflow: hidden;
    background: ${props => props.theme.colors.white};
`

const MessagesContainer = styled.div`
    flex: 1;
    overflow-y: auto;
    padding: 1rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
`

const Message = styled.div<{ isUser: boolean }>`
    max-width: 80%;
    padding: 0.75rem 1rem;
    border-radius: 8px;
    align-self: ${props => (props.isUser ? 'flex-end' : 'flex-start')};
    background: ${props =>
        props.isUser ? props.theme.colors.primary : props.theme.colors.backgroundPrimary};
    color: ${props => (props.isUser ? props.theme.colors.white : props.theme.colors.textPrimary)};
    white-space: pre-wrap;
    word-break: break-word;
`

const AIMDMessage = styled.div`
    a {
        color: ${props => props.theme.colors.textSecondary};
        font-weight: normal;
    }
`

const InputContainer = styled.form`
    display: flex;
    gap: 0.5rem;
    padding: 1rem;
    border-top: 1px solid ${props => props.theme.colors.border};
    background: ${props => props.theme.colors.backgroundPrimary};
`

const Input = styled.textarea`
    flex: 1;
    padding: 0.75rem;
    border: 1px solid ${props => props.theme.colors.border};
    border-radius: 4px;
    font-size: 1rem;
    resize: none;
    min-height: 44px;
    max-height: 120px;
    font-family: inherit;

    &:focus {
        outline: none;
        border-color: ${props => props.theme.colors.primary};
    }

    &:disabled {
        background: ${props => props.theme.colors.backgroundPrimary};
    }
`

const SendButton = styled.button`
    padding: 0.75rem 1.5rem;
    background: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.white};
    border: none;
    border-radius: 4px;
    font-size: 1rem;
    cursor: pointer;
    transition: background 0.2s;

    &:hover:not(:disabled) {
        background: ${props => props.theme.colors.textPrimary};
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`

const LoadingIndicator = styled.div`
    align-self: flex-start;
    padding: 0.75rem 1rem;
    border-radius: 8px;
    background: ${props => props.theme.colors.backgroundPrimary};
    color: ${props => props.theme.colors.textSecondary};
    font-style: italic;
`

interface ChatMessage {
    id: number
    text: string
    isUser: boolean
}

const AiChat: React.FC = (): React.ReactElement => {
    useAuthSnowplowPageView('AI Chat', 'AI Chat Page')

    const { user } = React.useContext(AuthContext)
    const [messages, setMessages] = useState<ChatMessage[]>([])
    const [input, setInput] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const messagesEndRef = useRef<HTMLDivElement>(null)

    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, [])

    useEffect(() => {
        scrollToBottom()
    }, [messages, scrollToBottom])

    const handleSubmit = useCallback(
        async (e: React.FormEvent) => {
            e.preventDefault()
            if (!input.trim() || isLoading) return

            const userMessage: ChatMessage = {
                id: Date.now(),
                text: input.trim(),
                isUser: true,
            }

            setMessages(prev => [...prev, userMessage])
            setInput('')
            setIsLoading(true)

            try {
                const response = await sendAiChatMessage(user, userMessage.text)
                const aiMessage: ChatMessage = {
                    id: Date.now() + 1,
                    text: response.response,
                    isUser: false,
                }
                setMessages(prev => [...prev, aiMessage])
            } catch (error) {
                const errorMessage: ChatMessage = {
                    id: Date.now() + 1,
                    text: 'Sorry, there was an error processing your request. Please try again.',
                    isUser: false,
                }
                setMessages(prev => [...prev, errorMessage])
            } finally {
                setIsLoading(false)
            }
        },
        [input, isLoading, user],
    )

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                handleSubmit(e as unknown as React.FormEvent)
            }
        },
        [handleSubmit],
    )

    return (
        <PageLayout>
            <Container>
                <Title>AI Assistant</Title>
                <ChatContainer>
                    <MessagesContainer>
                        {messages.length === 0 && (
                            <Message isUser={false}>
                                Hello! I can help you query the Neptune RDF database using natural
                                language. Ask me questions about titles, manifestations, or any data
                                in the ontology.
                            </Message>
                        )}
                        {messages.map(message => (
                            <Message key={message.id} isUser={message.isUser}>
                                {message.isUser ? (
                                    <>{message.text}</>
                                ) : (
                                    <AIMDMessage>
                                        <ReactMarkdown>{message.text}</ReactMarkdown>
                                    </AIMDMessage>
                                )}
                            </Message>
                        ))}
                        {isLoading && <LoadingIndicator>Thinking...</LoadingIndicator>}
                        <div ref={messagesEndRef} />
                    </MessagesContainer>
                    <InputContainer onSubmit={handleSubmit}>
                        <Input
                            value={input}
                            onChange={e => setInput(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Ask a question about titles, manifestations, or the ontology..."
                            disabled={isLoading}
                            rows={1}
                        />
                        <SendButton type="submit" disabled={isLoading || !input.trim()}>
                            Send
                        </SendButton>
                    </InputContainer>
                </ChatContainer>
            </Container>
        </PageLayout>
    )
}

export default AiChat
